import { createSlice, PayloadAction, current } from '@reduxjs/toolkit';
import { Routine, RoutineSuggestionPayload } from 'interfaces';
import {
  ActivityType,
  RoutineSuggestionsType
} from 'interfaces/common.interface';
import { initialRoutineSuggestions } from 'store/initial-states';
import {
  getRefinedRoutineHabitsWithAI,
  getRoutineSuggestionActivities
} from './extra';
import {
  updateSuggestionsWithActivities,
  hasNonEmptyArrayData
} from 'utils/routine-suggestion.util';
import { t } from 'services/i18n.service';
import { toast } from 'react-toastify';

const routineSuggestions = createSlice({
  name: 'routine-suggestions',
  initialState: initialRoutineSuggestions,
  reducers: {
    updateIsFetchingRoutineSuggestions: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isFetchingRoutineSuggestions = payload;
    },
    updateRoutineSuggestions: (
      state,
      { payload }: PayloadAction<RoutineSuggestionsType>
    ) => {
      state.routineSuggestions = payload;
      state.rawRoutineSuggestions = payload;
    },
    updateRoutineSuggestionsActivities: (
      state,
      { payload }: PayloadAction<ActivityType[]>
    ) => {
      state.routineSuggestions = updateSuggestionsWithActivities(
        current(state.routineSuggestions),
        payload
      );
    },
    updateRoutineSuggestionPayload: (
      state,
      { payload }: PayloadAction<RoutineSuggestionPayload>
    ) => {
      state.routineSuggestionPayload = payload;
    },
    updateRoutineSuggestionRoutinesPayload: (
      state,
      { payload }: PayloadAction<Routine[]>
    ) => {
      state.routineSuggestionPayload.routines = payload;
    },
    updateAreSuggestedHabitPacksInstalled: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.areSuggestedHabitPacksInstalled = payload;
    },
    updateIsInstallingRoutineSuggestions: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isInstallingRoutineSuggestions = payload;
    },
    updateShowEditSuggestionsModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showEditSuggestionsModal = payload;
    },
    updateSelectedGoals: (state, { payload }: PayloadAction<string[]>) => {
      state.selectedGoals = payload;
    },
    updateAvailableGoals: (
      state,
      { payload }: PayloadAction<Record<string, string[]>>
    ) => {
      state.availableGoals = payload;
    },
    updateRoutineSuggestionUserGoalsPayload: (
      state,
      { payload }: PayloadAction<string[]>
    ) => {
      state.routineSuggestionPayload.user_goals = payload;
    },
    updateConfirmSetupExistingHabits: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.confirmSetupExistingHabits = payload;
    },
    updateIsRefiningRoutineHabits: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isRefiningRoutineHabits = payload;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(
        getRoutineSuggestionActivities.fulfilled,
        (state, { payload }) => {
          if (
            payload &&
            hasNonEmptyArrayData(payload as Record<string, ActivityType[]>)
          ) {
            state.routineSuggestions = payload;
            state.rawRoutineSuggestions = payload;
          } else {
            toast.info(t('routine_suggestion.no_suggestions_found'));
          }
          state.isFetchingRoutineSuggestions = false;
        }
      )
      .addCase(getRoutineSuggestionActivities.rejected, (state) => {
        state.isFetchingRoutineSuggestions = false;
      })
      .addCase(getRefinedRoutineHabitsWithAI.fulfilled, (state) => {
        state.isRefiningRoutineHabits = false;
      })
      .addCase(getRefinedRoutineHabitsWithAI.rejected, (state) => {
        state.isRefiningRoutineHabits = false;
      });
  }
});

export const {
  updateIsFetchingRoutineSuggestions,
  updateRoutineSuggestions,
  updateAreSuggestedHabitPacksInstalled,
  updateIsInstallingRoutineSuggestions,
  updateRoutineSuggestionPayload,
  updateShowEditSuggestionsModal,
  updateAvailableGoals,
  updateSelectedGoals,
  updateRoutineSuggestionUserGoalsPayload,
  updateConfirmSetupExistingHabits,
  updateRoutineSuggestionsActivities,
  updateRoutineSuggestionRoutinesPayload,
  updateIsRefiningRoutineHabits
} = routineSuggestions.actions;

export default routineSuggestions.reducer;
